import { createQueryKeys } from '@lukemorales/query-key-factory';

import type {
  FetchLockerKeyAssignmentsArgs,
  FetchLockerKeyBoardArgs,
  FetchLockerKeyDisabledNumbersArgs,
  FetchLockerKeyGroupsArgs,
  FetchLockerKeyHoldersArgs,
  FetchLockerKeyOverdueCountArgs,
  LockerKeyScope,
  SearchLockerKeysArgs,
} from '../api/types/lockerKeys.types';

import {
  fetchLockerKeyAssignments,
  fetchLockerKeyBoard,
  fetchLockerKeyDisabledNumbers,
  fetchLockerKeyGroups,
  fetchLockerKeyHolders,
  fetchLockerKeyOverdueCount,
  searchLockerKeys,
} from '../api/lockerKeys.api';

type Filters<T> = Omit<T, 'organizationId'>;

// Every key is `[tenantId, organizationId, filters]` behind its name, so one
// scope's entries are one prefix (`[...name._def, tenantId, organizationId]`)
// for invalidation and placeholder checks — and a tenant switch can never
// read another tenant's cached board.
export const lockerKeysQueryKeys = createQueryKeys('lockerKeys', {
  board: (
    scope: LockerKeyScope,
    filters: Filters<FetchLockerKeyBoardArgs>,
  ) => ({
    queryFn: () =>
      fetchLockerKeyBoard({ ...filters, organizationId: scope.organizationId }),
    queryKey: [scope.tenantId, scope.organizationId, filters],
  }),
  disabledNumbers: (
    scope: LockerKeyScope,
    filters: Filters<FetchLockerKeyDisabledNumbersArgs>,
  ) => ({
    queryFn: () =>
      fetchLockerKeyDisabledNumbers({
        ...filters,
        organizationId: scope.organizationId,
      }),
    queryKey: [scope.tenantId, scope.organizationId, filters],
  }),
  groups: (
    scope: LockerKeyScope,
    filters: Filters<FetchLockerKeyGroupsArgs>,
  ) => ({
    queryFn: () =>
      fetchLockerKeyGroups({
        ...filters,
        organizationId: scope.organizationId,
      }),
    queryKey: [scope.tenantId, scope.organizationId, filters],
  }),
  history: (
    scope: LockerKeyScope,
    filters: Filters<FetchLockerKeyAssignmentsArgs>,
  ) => ({
    queryFn: () =>
      fetchLockerKeyAssignments({
        ...filters,
        organizationId: scope.organizationId,
      }),
    queryKey: [scope.tenantId, scope.organizationId, filters],
  }),
  holders: (
    scope: LockerKeyScope,
    filters: Filters<FetchLockerKeyHoldersArgs>,
  ) => ({
    queryFn: () =>
      fetchLockerKeyHolders({
        ...filters,
        organizationId: scope.organizationId,
      }),
    queryKey: [scope.tenantId, scope.organizationId, filters],
  }),
  // Org-wide on purpose: no group, search or state filter narrows it.
  overdueCount: (
    scope: LockerKeyScope,
    filters: Filters<FetchLockerKeyOverdueCountArgs> = {},
  ) => ({
    queryFn: () =>
      fetchLockerKeyOverdueCount({ organizationId: scope.organizationId }),
    queryKey: [scope.tenantId, scope.organizationId, filters],
  }),
  search: (scope: LockerKeyScope, filters: Filters<SearchLockerKeysArgs>) => ({
    queryFn: () =>
      searchLockerKeys({ ...filters, organizationId: scope.organizationId }),
    queryKey: [scope.tenantId, scope.organizationId, filters],
  }),
});
